import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, Clock, Loader2, Shield, CheckCircle2, Lock } from 'lucide-react';
import config from '../config';
import { useUser } from '../contexts/UserContext';
import { getTenantConfig } from '../tenants';

interface AccessData {
  requestId: string;
  dealId: string;
  dealTitle: string;
  dealSlug: string;
  minimumInvestment: number;
  targetRaise: number;
  currentRaised: number;
  dealMetadata: any;
  investorEmail: string;
  investorName: string;
  expiresAt: string;
}

/**
 * Private invest access page — non-accredited investors use this to access
 * offerings they've been approved for via admin-sent email links.
 *
 * Route: /invest-access/:token
 *
 * Flow: Resolve token → show deal info → user clicks "Begin Investment" →
 * stores access token in sessionStorage → redirects to normal deal invest page.
 * The API layer includes the token as x-invest-access-token header to bypass accreditation.
 */
const PrivateInvestAccess: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUser();
  const tenant = getTenantConfig();
  const brandName = tenant?.branding?.name || 'Platform';

  const [loading, setLoading] = useState(true);
  const [accessData, setAccessData] = useState<AccessData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccessData = async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/offering-access/invest/${token}`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.message || 'This investment link is invalid or has expired.');
          return;
        }
        setAccessData(data.data);
      } catch {
        setError('Unable to load investment details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchAccessData();
  }, [token]);

  const handleBeginInvestment = () => {
    if (!accessData || !token) return;
    // Store access token so the API layer can include it in requests
    sessionStorage.setItem('investAccessToken', token);
    // Navigate to the deal invest page
    navigate(`/portal/invest/offering/${accessData.dealSlug}`);
  };

  // Must be logged in
  if (!loading && !error && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-xl p-8 max-w-md w-full border border-border shadow-xl text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Login Required</h2>
          <p className="text-muted-foreground">
            Please log in to your account to access this private investment opportunity.
          </p>
          <Link
            to="/login"
            state={{ from: { pathname: `/invest-access/${token}` } }}
            className="inline-block w-full py-3 px-6 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Log In
          </Link>
        </div>
      </div>
    );
  }

  // Email mismatch check
  if (!loading && !error && accessData && user && user.email !== accessData.investorEmail) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-xl p-8 max-w-md w-full border border-border shadow-xl text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
            <Shield className="w-7 h-7 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold">Account Mismatch</h2>
          <p className="text-muted-foreground">
            This investment link was sent to <strong>{accessData.investorEmail}</strong>. 
            You are currently logged in as <strong>{user.email}</strong>.
          </p>
          <p className="text-sm text-muted-foreground">
            Please log in with the correct account to proceed.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading investment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-xl p-8 max-w-md w-full border border-border shadow-xl text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold">Link Unavailable</h2>
          <p className="text-muted-foreground">{error}</p>
          <Link
            to="/portal/invest/properties"
            className="inline-block text-primary hover:underline text-sm"
          >
            Browse Available Offerings
          </Link>
        </div>
      </div>
    );
  }

  if (!accessData) return null;

  // Landing / confirmation page
  const expiresAt = new Date(accessData.expiresAt);
  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card rounded-xl p-8 max-w-lg w-full border border-border shadow-xl space-y-6">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-semibold">You're Approved</h1>
          <p className="text-muted-foreground">
            You've been granted private access to invest in this offering.
          </p>
        </div>

        <div className="bg-muted/50 rounded-lg p-5 space-y-3">
          <h2 className="text-lg font-semibold">{accessData.dealTitle}</h2>
          {accessData.minimumInvestment && (
            <p className="text-sm text-muted-foreground">
              Minimum Investment: <span className="font-medium text-foreground">
                ${(accessData.minimumInvestment / 100).toLocaleString()}
              </span>
            </p>
          )}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>This link expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <button
          onClick={handleBeginInvestment}
          className="w-full py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg
            hover:bg-primary/90 transition-colors"
        >
          Begin Investment
        </button>

        <p className="text-center text-xs text-muted-foreground">
          This is a private investment link. By proceeding, you acknowledge that you have been 
          granted access to this offering as a non-accredited investor subject to applicable regulations.
        </p>
      </div>
    </div>
  );
};

export default PrivateInvestAccess;
